// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { BankAccount } from '@client/banks/types/account';
import type { BankUploadForm } from '@client/banks/types/create-bank-form';
import type { BankDebt } from '@client/banks/types/debt';
import type { BankPosition } from '@client/banks/types/position';
import type { BankTransaction } from '@client/banks/types/transaction';
import { appendLinkedAccount } from '@client/banks/utils/delete-account-and-unlink-instruments';
import produce from 'immer';

export type LinkedInstrumentSection = 'positions' | 'debts' | 'transactions';

type InstrumentBySection = {
  positions: BankPosition;
  debts: BankDebt;
  transactions: BankTransaction;
};

const upsertListItem = <T>(list: T[], index: number, item: T): T[] => {
  if (index < list.length) {
    const next = [...list];

    next[index] = item;

    return next;
  }

  return [item, ...list];
};

/** Atomically appends an optional new liquidity account and upserts the instrument. */
export function commitInstrumentWithLinkedAccount(
  form: BankUploadForm,
  section: 'positions',
  index: number,
  instrument: BankPosition,
  newAccount?: BankAccount,
): BankUploadForm;
export function commitInstrumentWithLinkedAccount(
  form: BankUploadForm,
  section: 'debts',
  index: number,
  instrument: BankDebt,
  newAccount?: BankAccount,
): BankUploadForm;
export function commitInstrumentWithLinkedAccount(
  form: BankUploadForm,
  section: 'transactions',
  index: number,
  instrument: BankTransaction,
  newAccount?: BankAccount,
): BankUploadForm;
export function commitInstrumentWithLinkedAccount<
  S extends LinkedInstrumentSection,
>(
  form: BankUploadForm,
  section: S,
  index: number,
  instrument: InstrumentBySection[S],
  newAccount?: BankAccount,
): BankUploadForm {
  return produce(form, draft => {
    let instrumentIban = instrument.iban;

    if (newAccount) {
      const appended = appendLinkedAccount(draft.accounts, newAccount);

      draft.accounts = appended.accounts;
      instrumentIban = appended.instrumentIban;
    }

    const item = { ...instrument, iban: instrumentIban };

    if (section === 'positions') {
      draft.positions = upsertListItem(
        draft.positions ?? [],
        index,
        item as BankPosition,
      );

      return;
    }

    if (section === 'debts') {
      draft.debts = upsertListItem(draft.debts ?? [], index, item as BankDebt);

      return;
    }

    draft.transactions = upsertListItem(
      draft.transactions ?? [],
      index,
      item as BankTransaction,
    );
  });
}
